import type { ReactNode } from 'react';

import { useTimeZone } from '@/app/preferences';
import { OutcomeBadge } from '@/components/badges';
import { DisclosureChevron, ExternalLink, Gap, Warning } from '@/components/icons';
import { usePresence } from '@/components/motion';
import { Table, Td, Th } from '@/components/primitives';
import { Timestamp } from '@/components/Timestamp';
import { formatClockZone, formatDuration, formatMillis } from '@/domain/time';
import type { BlindWindow, JudgedRun, QuarantineEntry } from '@/domain/types';

import { humaniseReason, orderChecks, refusedRowId, runDurationMs, runRowId } from './timeline';

export const COLUMNS = 6;

const TOGGLE =
  'inline-flex size-6 items-center justify-center rounded-sm text-ink-2 transition-tone hover:bg-surface-3 hover:text-ink';

/**
 * The row beneath a run or a refusal that holds its details. It opens and closes by height
 * and stays in the table until it has finished closing.
 */
function DetailRow({
  id,
  open,
  className,
  children,
}: {
  id: string;
  open: boolean;
  className: string;
  children: ReactNode;
}) {
  const { present, ref } = usePresence<HTMLDivElement>(open);
  if (!present) return null;
  return (
    <tr id={id}>
      <td colSpan={COLUMNS} className="p-0">
        <div ref={ref} className="reveal" data-open={open || undefined}>
          <div>
            <div className={className}>{children}</div>
          </div>
        </div>
      </td>
    </tr>
  );
}

export function RunRow({
  run,
  expanded,
  onToggle,
  configuredChecks,
}: {
  run: JudgedRun;
  expanded: boolean;
  onToggle: () => void;
  configuredChecks: string[] | undefined;
}) {
  const tz = useTimeZone();
  const clock = formatClockZone(run.completed_at, tz);
  const rowId = runRowId(run.run_key);
  const detailId = `${rowId}-checks`;
  return (
    <>
      <tr
        id={rowId}
        data-testid="run-row"
        className={['transition-tone', expanded ? 'bg-surface-2' : 'hover:bg-surface-2'].join(' ')}
      >
        <Td className="w-12 pl-5 pr-0">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? detailId : undefined}
            aria-label={`${expanded ? 'Hide' : 'Show'} checks for the run completed at ${clock}`}
            onClick={onToggle}
            className={TOGGLE}
          >
            <DisclosureChevron open={expanded} size={14} />
          </button>
        </Td>
        <Td>
          <OutcomeBadge outcome={run.judged.outcome} />
        </Td>
        <Td>
          <Timestamp iso={run.completed_at} />
        </Td>
        <Td className="tabular text-right whitespace-nowrap">{formatMillis(runDurationMs(run))}</Td>
        <Td className="break-words">
          {run.judged.driving_check ? (
            <code>{run.judged.driving_check}</code>
          ) : (
            <span className="text-ink-3">—</span>
          )}
        </Td>
        <Td className="pr-5 text-meta text-ink-3 break-all">
          {run.source} · {run.run_id}
        </Td>
      </tr>
      <DetailRow
        id={detailId}
        open={expanded}
        className="border-b border-line bg-surface-2 px-5 pb-4 pt-2"
      >
        {/* The checks sit in a white box on the tinted row, so their column heads keep their band. */}
        <div className="overflow-hidden rounded-sm border border-line bg-surface">
          <ChecksTable run={run} configuredChecks={configuredChecks} clock={clock} />
        </div>
      </DetailRow>
    </>
  );
}

function ChecksTable({
  run,
  configuredChecks,
  clock,
}: {
  run: JudgedRun;
  configuredChecks: string[] | undefined;
  clock: string;
}) {
  const checks = orderChecks(run.checks, configuredChecks);
  return (
    <Table
      aria-label={`Checks for the run completed at ${clock}`}
      className="text-meta [&>tbody>tr:last-child>td]:border-b-0"
    >
      <thead>
        <tr>
          <Th>Check</Th>
          <Th>Outcome</Th>
          <Th>Observed</Th>
          <Th className="text-right">Duration</Th>
          <Th>Error</Th>
          <Th>Logs</Th>
        </tr>
      </thead>
      <tbody>
        {checks.map((check) => (
          <tr key={check.name}>
            <Td className="break-all">
              <code>{check.name}</code>
            </Td>
            <Td>
              <OutcomeBadge outcome={check.outcome} />
            </Td>
            <Td>
              <Timestamp iso={check.observed_at} mode="clock" withSeconds />
            </Td>
            <Td className="tabular text-right whitespace-nowrap">
              {check.duration_ms !== undefined ? (
                formatMillis(check.duration_ms)
              ) : (
                <span className="text-ink-3">—</span>
              )}
            </Td>
            <Td className="max-w-[40ch] break-words">
              {check.error_code ? (
                <>
                  <code>{check.error_code}</code>
                  {check.error_message ? (
                    <span className="block text-ink-2">{check.error_message}</span>
                  ) : null}
                </>
              ) : (
                <span className="text-ink-3">—</span>
              )}
            </Td>
            <Td>
              {check.artifact_url ? (
                <a
                  href={check.artifact_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 whitespace-nowrap"
                >
                  Logs <ExternalLink size={12} />
                </a>
              ) : (
                <span className="text-ink-3">—</span>
              )}
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** A period the runner sent nothing (TDD §7): drawn where it falls between runs. */
export function GapRow({ window }: { window: BlindWindow }) {
  return (
    <tr className="bg-status-none-soft">
      <td
        colSpan={COLUMNS}
        className="border-b border-line px-4 py-2 text-body text-status-none-ink"
      >
        <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
          <Gap size={14} />
          {window.closed_at ? (
            <>
              <span className="font-medium">
                Blind for{' '}
                {formatDuration(Date.parse(window.closed_at) - Date.parse(window.opened_at))}
              </span>
              <span>· {window.missed_runs_at_close ?? '?'} runs missed</span>
              <span>
                · <Timestamp iso={window.opened_at} /> → <Timestamp iso={window.closed_at} />
              </span>
            </>
          ) : (
            <>
              <span className="font-medium">
                Blind since <Timestamp iso={window.opened_at} />
              </span>
              <span>
                ·{' '}
                {window.last_observed_at ? (
                  <>
                    last run <Timestamp iso={window.last_observed_at} />
                  </>
                ) : (
                  'never reported'
                )}
              </span>
            </>
          )}
        </span>
      </td>
    </tr>
  );
}

/** A payload intake refused (TDD §3): the runner sent something, the system did not store it. */
export function RefusedRow({
  entry,
  expanded,
  onToggle,
}: {
  entry: QuarantineEntry;
  expanded: boolean;
  onToggle: () => void;
}) {
  const tz = useTimeZone();
  const clock = formatClockZone(entry.received_at, tz);
  const rowId = refusedRowId(entry.quarantine_id);
  const detailId = `${rowId}-payload`;
  return (
    <>
      <tr id={rowId} data-testid="refused-row" className="bg-status-impaired-soft">
        <Td className="w-12 pl-5 pr-0">
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={expanded ? detailId : undefined}
            aria-label={`${expanded ? 'Hide' : 'Show'} the payload refused at ${clock}`}
            onClick={onToggle}
            className={TOGGLE}
          >
            <DisclosureChevron open={expanded} size={14} />
          </button>
        </Td>
        {/* One cell across the run columns, like a gap row: the column headers do not apply to a refusal. */}
        <Td colSpan={COLUMNS - 1} className="pr-5 break-words">
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="inline-flex items-center gap-1.5 font-medium text-status-impaired-ink whitespace-nowrap">
              <Warning size={14} /> Sent but rejected
            </span>
            <span>
              · <Timestamp iso={entry.received_at} />
            </span>
            <span className="tabular whitespace-nowrap">· HTTP {entry.http_status}</span>
            <span>
              · <span className="font-medium">{humaniseReason(entry.reason)}</span>
            </span>
          </span>
          <span className="block text-ink-2">{entry.detail}</span>
          <span className="block text-meta text-ink-3">from {entry.source}</span>
        </Td>
      </tr>
      <DetailRow
        id={detailId}
        open={expanded}
        className="border-b border-line bg-status-impaired-soft px-4 pb-3"
      >
        {entry.raw_payload ? (
          <pre className="m-0 overflow-x-auto whitespace-pre-wrap break-all rounded-sm border border-line bg-surface p-3 text-meta text-ink">
            {entry.raw_payload}
          </pre>
        ) : (
          <p className="m-0 text-body text-ink-3">The payload was not kept.</p>
        )}
        {entry.raw_payload_truncated ? (
          <p className="mt-1 mb-0 text-meta text-ink-3">(truncated)</p>
        ) : null}
      </DetailRow>
    </>
  );
}
